import { useState } from "react";
import { motion, AnimatePresence } from "framer-motion";

const ACTIONS = [
  {
    id: "sleep",
    label: "SLEEP +1H EVERY NIGHT",
    good: "M20 200 C120 185 240 130 380 60",
    base: "M20 200 C120 200 240 202 380 206",
    stats: [
      { k: "HRV", v: "+9 MS", up: true },
      { k: "RESTING HR", v: "-4 BPM", up: true },
      { k: "STRESS LOAD", v: "-18%", up: true },
    ],
    line: "The twin projects recovery compounding within weeks · before you feel it.",
  },
  {
    id: "skip",
    label: "SKIP WORKOUTS FOR 30 DAYS",
    good: "M20 200 C120 205 240 225 380 252",
    base: "M20 200 C120 195 240 185 380 172",
    stats: [
      { k: "VO₂ TREND", v: "-6%", up: false },
      { k: "SLEEP QUALITY", v: "-11%", up: false },
      { k: "ENERGY", v: "DECLINING", up: false },
    ],
    line: "Nothing changes on day one. The twin shows you day thirty.",
  },
  {
    id: "walk",
    label: "WALK 8K STEPS DAILY",
    good: "M20 200 C120 190 240 150 380 96",
    base: "M20 200 C120 200 240 203 380 208",
    stats: [
      { k: "RESTING HR", v: "-3 BPM", up: true },
      { k: "STEPS AVG", v: "6.9K → 8K", up: true },
      { k: "MOOD SIGNAL", v: "+14%", up: true },
    ],
    line: "Small input. The twin maps how it ripples through every system.",
  },
];

const ConsequenceSim = () => {
  const [act, setAct] = useState("sleep");
  const a = ACTIONS.find((x) => x.id === act);
  return (
    <section data-testid="consequence-section" className="relative px-6 py-24 md:px-12 md:py-32">
      <div className="mx-auto max-w-[1500px]">
        <p className="font-mono text-[11px] tracking-[0.28em] text-ash">04 · CONSEQUENCES</p>
        <h2
          data-testid="consequence-headline"
          className="mt-4 max-w-3xl font-display text-3xl font-light leading-tight text-white md:text-5xl"
        >
          If you knew the <span className="text-signal">impact</span> of
          your actions before they happened, would you change them?
        </h2>

        <div className="mt-10 flex flex-wrap gap-3">
          {ACTIONS.map((x) => (
            <button
              key={x.id}
              data-testid={`consequence-action-${x.id}`}
              onClick={() => setAct(x.id)}
              className={`rounded-full border px-5 py-3 font-mono text-[10px] tracking-[0.14em] transition-colors duration-300 ${
                act === x.id ? "border-signal bg-signal/15 text-white" : "border-line text-ash hover:border-fog hover:text-fog"
              }`}
            >
              {x.label}
            </button>
          ))}
        </div>

        <div className="mt-8 grid items-center gap-8 rounded-3xl border border-line bg-ink-2/40 p-6 md:p-10 lg:grid-cols-[1.4fr_1fr]">
          <svg viewBox="0 0 400 280" className="w-full" aria-hidden="true">
            <line x1="20" y1="20" x2="20" y2="260" stroke="#2A2026" />
            <line x1="20" y1="260" x2="390" y2="260" stroke="#2A2026" />
            <AnimatePresence mode="wait">
              <motion.g key={act} initial={{ opacity: 0 }} animate={{ opacity: 1 }} exit={{ opacity: 0 }} transition={{ duration: 0.4 }}>
                <motion.path d={a.base} stroke="#6E676A" strokeWidth="1" strokeDasharray="4 4" fill="none" initial={{ pathLength: 0 }} animate={{ pathLength: 1 }} transition={{ duration: 1 }} />
                <motion.path d={a.good} stroke="#D2378B" strokeWidth="2" fill="none" initial={{ pathLength: 0 }} animate={{ pathLength: 1 }} transition={{ duration: 1.2 }} />
              </motion.g>
            </AnimatePresence>
            <circle cx="20" cy="200" r="5" fill="#FFFFFF" />
            <text x="30" y="196" fontSize="10" fontFamily="JetBrains Mono" fill="#A19A9D">TODAY</text>
            <text x="388" y="274" fontSize="9" fontFamily="JetBrains Mono" fill="#6E676A" textAnchor="end">90 DAYS →</text>
            <text x="388" y="20" fontSize="9" fontFamily="JetBrains Mono" fill="#D2378B" textAnchor="end">WITH THE CHANGE</text>
            <text x="388" y="34" fontSize="9" fontFamily="JetBrains Mono" fill="#6E676A" textAnchor="end">WITHOUT</text>
          </svg>
          <div>
            <AnimatePresence mode="wait">
              <motion.div key={act} initial={{ opacity: 0, y: 12 }} animate={{ opacity: 1, y: 0 }} exit={{ opacity: 0 }} transition={{ duration: 0.35 }}>
                {a.stats.map((s) => (
                  <div key={s.k} className="flex items-baseline justify-between border-b border-line/50 py-4">
                    <p className="font-mono text-[10px] tracking-[0.18em] text-ash">{s.k}</p>
                    <p className={`font-display text-3xl font-extralight md:text-4xl ${s.up ? "text-signal" : "text-fog"}`}>{s.v}</p>
                  </div>
                ))}
                <p data-testid="consequence-note" className="mt-6 text-sm leading-relaxed text-fog">{a.line}</p>
                <p className="mt-4 font-mono text-[8px] tracking-[0.18em] text-ash/60">ILLUSTRATIVE PROJECTION · YOUR TWIN USES YOUR DATA</p>
              </motion.div>
            </AnimatePresence>
          </div>
        </div>
      </div>
    </section>
  );
};

export default ConsequenceSim;
